import { useBloc, useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import { Button } from "react-aria-components";
import { useNavigate } from "react-router-dom";
import { APP_CONTENT_WIDTH } from "src/constants/layout";
import DataHubBgImg from "src/ui/assets/images/dataHubBg.jpg";
import Translate from "src/ui/components/Translate/Translate";
import DataHubChart from "./DataHubChart";
import DataHubCubit from "./DataHubCubit";
import DataStatus from "./DataStatus";
import DataTabs from "./DataTabs";
import DataTimeRangeButtons from "./DataTimeRangeButtons";
import DataHubTitleRow from "./DataHubTitleRow";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import UserPreferencesCubit, {
  chartVisibilityDependencies
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";

//#region Styled Components
const DataHubWrapper = styled.div`
  position: relative;
  margin: -1rem calc(-1 * var(--space-md)) 1.5rem;
  padding: 2rem 1.5rem 1.25rem;
  box-shadow: var(--light-shadow);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: #fff;
    z-index: -2;
  }

  @media screen and (min-width: ${APP_CONTENT_WIDTH}px) {
    margin: 2rem 0 2rem;
    border-radius: 0.5rem;
  }

  /* Hide status if empty state is visible */
  &:has(.empty-state) .status {
    visibility: hidden;
  }

  /* There is no range to choose between when the chart has nothing to plot */
  &:has(.empty-state) .time-range-buttons {
    display: none;
  }

  .chart-tooltip-value {
    em {
      font-style: normal;
      opacity: 0.5;
    }
  }
`;

const StyledButton = styled(Button)`
  background: var(--color-gray-fog);
  border-radius: 0.5rem;
  margin-top: 1.25rem;
  width: 100%;
  color: var(--color-charcoal-80);
  height: 2.75rem;
`;

const StyledImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  aspect-ratio: 375/200;
  z-index: -1;
`;

const ChartWrapper = styled.div`
  height: 15rem;
  margin: 0 -1.25rem;

  &[data-blurred="true"] {
    filter: blur(0.25rem);
    user-select: none;
    pointer-events: none;
  }

  &[data-blurred="true"]:has(.empty-state) {
    filter: none;
    user-select: auto;
    pointer-events: auto;
  }
`;
//#endregion

const DataHub: FC = () => {
  const [{ activeLoincCode }, { getLoincCodesForUrl, activeChartBlurred }] =
    useBloc(DataHubCubit, {
      dependencySelector: (s) => [s.activeLoincCode]
    });
  const [{ partnerStyles }] = useBloc(AppViewCubit);

  useBlocNext(UserPreferencesCubit, {
    dependencySelector: chartVisibilityDependencies
  });
  const navigate = useNavigate();

  return (
    <DataHubWrapper className="data-hub">
      {/* Background */}
      {!partnerStyles && <StyledImg src={DataHubBgImg} />}

      {/* Section title and add entry button*/}
      <DataHubTitleRow />

      {/* Clickable tabs */}
      <DataTabs />

      {/* Status */}
      <DataStatus />

      {/* Chart */}
      <ChartWrapper data-blurred={activeChartBlurred}>
        <DataHubChart />
      </ChartWrapper>

      {/* Time range buttons */}
      <DataTimeRangeButtons />

      {/* Explore Data button */}
      <StyledButton
        onPress={() => {
          navigate(
            `/app/lab-results/list-codes/${getLoincCodesForUrl(activeLoincCode)}`
          );
        }}
      >
        <Translate msg="exploreData" />
      </StyledButton>
    </DataHubWrapper>
  );
};

export default DataHub;
